// Map debugging context menu for development
// https://docs.mapbox.com/mapbox-gl-js/api/map/#instance-members-debug-features
import { metersToFeet, formatNumber } from '@/components/shared/x-distance'
import { isActive } from '@/services/feature-flag-service'
import { getAccessToken } from '@/services/identity-client'
import { getItem, saveItem } from '@/plugins/storage'
// eslint-disable-next-line no-restricted-imports
import {
  getTimeToMapIdle,
  getTimeToMapLoad,
  getTimeToMapStyleLoad,
  getTimeForStyleInit,
} from '@/lib/map-viewer/map-performance'
import { info, enableGroup, enableLogging } from '@/lib/logger'
import { ref } from 'vue'
import { createHTMLElement } from '@/lib/map-x/create-html-element'
import MapElementWrapper from '@/components/map-x/map-element-wrapper.vue'
import { setHTMLElementToMapLocation } from '@/lib/map-x/anyhtml'
import XToolTip from '@/components/shared/x-tool-tip.vue'
import { getMapEvents } from '@/composables/map-x/use-map-events'

const contextMenuContentTemplate = ref('')
let contextMenuController = null

// Display bbox around search features
export let isSearchFeatureBoundsDebuggingEnabled = false

// Force logging on when using map debug menu
function enableMapLoggingAndlogInfo(...args) {
  enableLogging()
  enableGroup('Map')
  info('Map', ...args)
}
// Determine if menu is enabled
const isMenuEnabled = () => {
  // Enable for employee setting
  if (isActive('enable_debugging_menu')) {
    return true
  }
  // Enable in development mode
  // eslint-disable-next-line no-undef
  if (process.env.NODE_ENV === 'development') {
    return true
  }
  return false
}

export const addDebugContextMenu = (mapViewer) => {
  if (!isMenuEnabled()) {
    return
  }

  const map = mapViewer.map
  const eventEmitter = getMapEvents()
  if (!eventEmitter) {
    return
  }

  let tooltipDebugEnabled = getItem('tooltipDebug')

  // Show menu on right click
  eventEmitter.on('map-x-contextmenu', (event) => {
    const elevation = map.queryTerrainElevation(event.detail.coordinates, {
      exaggerated: false,
    })

    if (contextMenuController) {
      contextMenuController.destroy()
    }

    contextMenuContentTemplate.value = `
      <div class="close-button" style="text-align: right;">
        <button id="context-close-button" style="background: none; color: #666;">
          ✕
        </button>
      </div>
      <a id="context-queryRenderedFeatures" style="cursor: pointer"
        >Log Rendered Features</a
      >
      <a id="context-queryRenderedFeatures-copy-json" style="cursor: pointer"
        >(JSON📋)</a
      ><br />
      <a id="context-logStyleSheet" style="cursor: pointer"
        >Log Stylesheet</a
      >
      <a id="context-logStyleSheet-copy-json" style="cursor: pointer"
        >(JSON📋)</a
      ><br />
      <a id="context-copyAuthToken" style="cursor: pointer"
        >Auth Token📋</a
      >&nbsp;<span>|</span>&nbsp;
      <a id="context-copyBearerTokenString" style="cursor: pointer"
        >Bearer String 📋</a
      ><br />
      <div>
        <input type="checkbox" id="context-showTileBoundaries"
        name="context-showTileBoundaries" ${
          map.showTileBoundaries ? 'checked' : ''
        }>
        <label for="context-showTileBoundaries" style="display: inline"
          >Show Tile Boundaries</label
        >
      </div>
      <div>
        <input type="checkbox" id="context-showSearchFeatureBounds"
        name="context-showSearchFeatureBounds" ${
          isSearchFeatureBoundsDebuggingEnabled ? 'checked' : ''
        }>
        <label for="context-showSearchFeatureBounds" style="display: inline"
          >Show Search Feature Bounds</label
        >
      </div>
      <div>
        <input type="checkbox" id="context-showTerrainWireframe"
        name="context-showTerrainWireframe" ${
          map.showTerrainWireframe ? 'checked' : ''
        }>
        <label for="context-showTerrainWireframe" style="display: inline"
          >Show Terrain Wireframe</label
        >
      </div>
      <div>
        <input type="checkbox" id="context-showPadding" name="context-showPadding" ${
          map.showPadding ? 'checked' : ''
        }>
        <label for="context-showPadding" style="display: inline">Show Padding</label>
      </div>
      <div>
        <input type="checkbox" id="context-showCollisionBoxes"
        name="context-showCollisionBoxes" ${
          map.showCollisionBoxes ? 'checked' : ''
        }>
        <label for="context-showCollisionBoxes" style="display: inline"
          >Show Collision Boxes</label
        >
      </div>
      <div>
        <input type="checkbox" id="context-repaint" name="context-repaint" ${
          map.repaint ? 'checked' : ''
        }>
        <label for="context-repaint" style="display: inline">Repaint</label>
      </div>
      <div>
        <input type="checkbox" id="context-tooltipDebug" name="context-tooltipDebug" ${
          tooltipDebugEnabled ? 'checked' : ''
        }>
        <label for="context-tooltipDebug" style="display: inline">Tooltip Debug window</label>
      </div>
      <div>Elevation: ${
        elevation
          ? `${formatNumber(0)(metersToFeet(elevation))} ft`
          : 'No Terrain'
      }</div>
      ${
        getTimeForStyleInit()
          ? `<div>Map Style Init Time: ${formatNumber(3)(
              getTimeForStyleInit() / 1000
            )} seconds </div>`
          : ``
      }
      ${
        getTimeToMapStyleLoad()
          ? `<div>Map Style Load Time: ${formatNumber(3)(
              getTimeToMapStyleLoad() / 1000
            )} seconds </div>`
          : ``
      }
      ${
        getTimeToMapLoad()
          ? `<div>Map Load Time: ${formatNumber(3)(
              getTimeToMapLoad() / 1000
            )} seconds </div>`
          : ``
      }
      ${
        getTimeToMapIdle()
          ? `<div>Map Idle Time: ${formatNumber(3)(
              getTimeToMapIdle() / 1000
            )} seconds </div>`
          : ``
      }
      <div>Mapbox Version: ${map.version}</div>`

    const contextMenuElement = createHTMLElement(MapElementWrapper, {
      vueComponent: XToolTip,
      vueComponentProps: {
        trigger: 'always',
        delay: 0,
        direction: 'top',
        style: 'pointer-events: initial;',
      },
      templateLiteral: contextMenuContentTemplate.value,
      style: 'text-align: left; padding: 7px 2px 12px;',
    })

    /**
     * set the HTMLElement to map location and create a element controller
     * contextMenuController
     */
    contextMenuController = setHTMLElementToMapLocation(
      contextMenuElement,
      event.detail.coordinates,
      mapViewer,
      eventEmitter,
      'map-x-render',
      (next) => (contextMenuController = next)
    )

    // Setup event listeners
    const addClickHandler = (id) => (callback) =>
      document.getElementById(id).addEventListener('click', callback)

    // Close button handler
    addClickHandler('context-close-button')(() => {
      destroyContextMenu()
    })

    addClickHandler('context-repaint')(() => {
      //https://docs.mapbox.com/mapbox-gl-js/api/map/#map#repaint
      map.repaint = !map.repaint
    })

    addClickHandler('context-tooltipDebug')(() => {
      tooltipDebugEnabled = !tooltipDebugEnabled
      saveItem('tooltipDebug', tooltipDebugEnabled)
    })

    addClickHandler('context-showPadding')(() => {
      // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#showpadding
      map.showPadding = !map.showPadding
    })
    addClickHandler('context-showCollisionBoxes')(() => {
      // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#showcollisionboxes
      map.showCollisionBoxes = !map.showCollisionBoxes
    })
    addClickHandler('context-showTileBoundaries')(() => {
      // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#showtileboundaries
      map.showTileBoundaries = !map.showTileBoundaries
    })
    addClickHandler('context-showSearchFeatureBounds')(() => {
      // Show a bbox around search features
      isSearchFeatureBoundsDebuggingEnabled =
        !isSearchFeatureBoundsDebuggingEnabled
    })
    addClickHandler('context-showTerrainWireframe')(() => {
      //https://docs.mapbox.com/mapbox-gl-js/api/map/#map#showterrainwireframe
      map.showTerrainWireframe = !map.showTerrainWireframe
    })
    addClickHandler('context-queryRenderedFeatures')(() => {
      const features = map.queryRenderedFeatures(event.detail.point)
      enableMapLoggingAndlogInfo('Rendered Features:', features)
    })
    addClickHandler('context-queryRenderedFeatures-copy-json')(() => {
      const features = map.queryRenderedFeatures(event.detail.point)
      const json = JSON.stringify(features)
      navigator.clipboard.writeText(json)
      enableMapLoggingAndlogInfo('Rendered Features Copied to 📋:', json)
    })
    addClickHandler('context-logStyleSheet')(() => {
      enableMapLoggingAndlogInfo('Stylesheet:', map.getStyle())
    })
    addClickHandler('context-logStyleSheet-copy-json')(() => {
      const stylesheet = map.getStyle()
      const json = JSON.stringify(stylesheet)
      navigator.clipboard.writeText(json)
      enableMapLoggingAndlogInfo('Stylesheet Copied to 📋:', json)
    })
    addClickHandler('context-copyAuthToken')(async () => {
      const token = await getAccessToken()
      navigator.clipboard.writeText(token)
    })
    addClickHandler('context-copyBearerTokenString')(async () => {
      const token = await getAccessToken()
      const bearerTokenString = `Bearer ${token}`
      navigator.clipboard.writeText(bearerTokenString)
    })

    /**
     * Destroy on click outside. Mapbox's click event distinguishes
     * between clicks and drags (click won't fire after a drag).
     */
    const destroyContextMenu = () => {
      if (contextMenuController) {
        contextMenuController.destroy()
        eventEmitter.off('map-x-click', destroyContextMenu)
      }
    }

    eventEmitter.once('map-x-click', destroyContextMenu)
  })
}
